import React, { Fragment, useEffect, useState } from "react";
import { ArrowForwardIosSharp } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  IconButton,
  Typography,
  styled,
} from "@mui/material";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditNoteRoundedIcon from "@mui/icons-material/EditNoteRounded";
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import { useTranslation } from "react-i18next";
import { getName } from "../../../../../../utils/userData";

const AccordionStyled = styled((props) => (
  <Accordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: "none",
  "&:not(:last-child)": {
    borderBottom: 0,
  },
  "&:before": {
    display: "none",
  },
  "& .header-leave-type": {
    paddingLeft: "8px",
    borderLeft: "8px solid #1976D2",
    borderTopLeftRadius: "4px",
    borderBottomLeftRadius: "4px",
  }
}));

const AccordionSummaryStyled = styled((props) => (
  <AccordionSummary
    expandIcon={<ArrowForwardIosSharp sx={{ fontSize: "0.9rem" }} />}
    {...props}
  />
))(({ theme }) => ({
  width: "100%",
  borderRadius: 8,
  marginBottom: 8,
  backgroundColor:
    theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, .05)"
      : "rgba(0, 0, 0, .03)",
  flexDirection: "row-reverse",
  "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
    transform: "rotate(90deg)",
  },
  "& .MuiAccordionSummary-content": {
    marginLeft: theme.spacing(1),
  },
  "& .wrap": {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
}));

const AccordionRow = (props) => {
  const { data, setDrawerConfig, handleOpenDialogDelete, handleOpenLeavePolicyVersionModal } = props;
  const [isExpand, setIsExpand] = useState(false);
  const { t } = useTranslation();
  //   const response = await dispatch(
  //     deleteVendorCompanyLeaveType(data.idLeaveType)
  //   );
  //   if (response.status === 200) {
  //     setAlertType("success");
  //     let result = await dispatch(
  //       getAllVendorCompanyLeaveType({
  //         idCompany: selectedCompany.idCompany,
  //         idVendor: userProfile.idVendor,
  //       })
  //     );
  //     handleCloseDialog();
  //   } else {
  //     setAlertType("error");
  //   }
  //   setOpenAlert(true);
  // };

  return (
    <AccordionStyled
      expanded={isExpand}
      onChange={() => {setIsExpand((prev) => !prev)}}
    >
      <AccordionSummaryStyled>
        <Box flexGrow="1" display="flex" alignItems="center">
          <Typography flexGrow="1">
            {getName({name: data.leaveGroupName, name_EN: data.leaveGroupName_EN})}
          </Typography>

          <Box>
            <IconButton
              aria-lable="view_policy_version"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenLeavePolicyVersionModal(data.idLeaveGroup);
              }}
            >
              <HistoryRoundedIcon fontSize="inherit" />
            </IconButton>

            <IconButton
              aria-label="edit"
              onClick={(e) => {
                e.stopPropagation();
                setDrawerConfig((prev) => ({
                  ...prev,
                  isOpen: true,
                  isEdit: true,
                  data: {
                    ...data,
                  },
                }));
              }}
            >
              <EditNoteRoundedIcon fontSize="inherit" />
            </IconButton>
            <IconButton
              aria-label="delete"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenDialogDelete(data);
              }}
            >
              <DeleteOutlineRoundedIcon fontSize="inherit" />
            </IconButton>
          </Box>
        </Box>
      </AccordionSummaryStyled>
      <AccordionDetails>
        <Grid container spacing={2}>
          {data.leaveTypeList.map((item, index) => (
            <Grid key={item.idLeaveType} item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box display="flex" alignItems="center">
                    <Box className="header-leave-type">
                      <Typography fontSize="18px" fontWeight="600">{t("EmployeeGroup")} {index + 1}</Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography paddingBottom="4px" color="text.secondary" fontSize="14px">{t("EmployeeType")}</Typography>
                  {item.employeeTypeList.length === 0? (
                    <Typography>{t("All")}</Typography>
                  ): (
                    <Typography>{item.employeeTypeList.map(x => `${x.employeeTypeName} (${x.companyNameShort})`).join(", ")}</Typography>
                  )}
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography paddingBottom="4px" color="text.secondary" fontSize="14px">{t("PersonalLevel")}</Typography>
                  {item.personnelLevelGroupList.length === 0? (
                    <Typography>{t("All")}</Typography>
                  ): (
                    <Typography>{item.personnelLevelGroupList.map(x => `${x.groupLevelName} (${x.companyNameShort})`).join(", ")}</Typography>
                  )}
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography paddingBottom="4px" color="text.secondary" fontSize="14px">{t("TotalLeaveDays")}</Typography>
                  <Typography>
                    {item.isLeaveStep
                      ? t("OnCondition")
                      : item.leaveValue === null
                        ? t("Leave.Unlimited")
                        : `${item.leaveValue} ${t("Days")}`
                    }
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography paddingBottom="4px" color="text.secondary" fontSize="14px">{t("AccumulateNextRoundNotMoreThan")}</Typography>
                  <Typography>
                    {item.isLeaveStep
                      ? t("OnCondition")
                      : item.carryValue === null
                        ? t("Leave.Unlimited")
                        : `${item.carryValue} ${t("Days")}`
                    }
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography paddingBottom="4px" color="text.secondary" fontSize="14px">{t("Pay")}</Typography>
                  <Typography>{`${item.isPaid? t("Yes"): t("No")} ${item.payPercentage !== null? `(${item.payPercentage}%)`: ""}`}</Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography paddingBottom="4px" color="text.secondary" fontSize="14px">{t("CanRequestLeaveAfter")}</Typography>
                  <Typography>
                    {item.canRequestAfter === null
                      ? `${t("StartWork")}`
                      : item.canRequestAfter < 365
                        ? ` ${item.canRequestAfter} ${t("Days")}`
                        : `${t("YearOfService")} ${item.canRequestAfter / 365} ${t("Year")}`
                    }
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography paddingBottom="4px" color="text.secondary" fontSize="14px">{t("Gender")}</Typography>
                  <Typography>{item.gender? `${item.gender}`: `${t("AllGender")}`}</Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography paddingBottom="4px" color="text.secondary" fontSize="14px">{t("MinimumLeaveAllowed")}</Typography>
                  <Typography>
                    {item.minLeave === 100
                      ? `${t("FullDay")}`
                      : item.minLeave === 50
                      ? `${t("HalfDay")}`
                      : item.minLeave === 10
                      ? `1 ${t("Hour")}`
                      : item.minLeave === 5
                      ? `${t("HalfHour")}`
                      : "-"
                    }
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography paddingBottom="4px" color="text.secondary" fontSize="14px">Prorate</Typography>
                  <Typography>
                    {item.isProRate ? t("Yes") : t("No")}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography paddingBottom="4px" color="text.secondary" fontSize="14px">{`${t("Rounding")} Prorate`}</Typography>
                  <Typography>
                    {item.proRateRound === 1
                      ? t("ThereIsNoRounding")
                      : item.proRateRound === 0.5
                      ? `${t("RoundWhenGreaterThan")} 0.5`
                      : item.proRateRound === 0
                      ? t("RoundUpInAllCases")
                      : "-"
                    }
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography paddingBottom="4px" color="text.secondary" fontSize="14px">{t("LeaveIn")}</Typography>
                  <Typography>
                    {item.dayInAdvance
                      ? `${item.dayInAdvance} ${t("Days")}`
                      : t("NotSpecified")
                    }
                  </Typography>
                </Grid>

                {item.isLeaveStep === 1 && (
                  <Fragment>
                    <Grid item xs={12}>
                      <Typography color="text.secondary" fontSize="18px">{t("Condition")}</Typography>
                    </Grid>
                    {item.leaveStepList.map((step, stepIndex) => (
                      <Fragment key={`${item.idLeaveType}_${stepIndex}`}>
                        <Grid item xs={12} md={4}>
                          <Typography paddingBottom="4px" color="text.secondary" fontSize="14px">{t("YearOfService")}</Typography>
                          <Typography>
                            {`${step.lessThanYear}${
                              step.moreThanYears === 99
                                ? " ปีขึ้นไป"
                                : ` - ${step.moreThanYears} ${t("Year")}`
                            }`}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Typography paddingBottom="4px" color="text.secondary" fontSize="14px">{t("TotalLeaveDays")}</Typography>
                          <Typography>{step.leaveValue !== null? `${step.leaveValue} ${t("Days")}`: t("Leave.Unlimited")}</Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Typography paddingBottom="4px" color="text.secondary" fontSize="14px">{t("AccumulateNextRoundNotMoreThan")}</Typography>
                          <Typography>{step.carryValue !== null? `${step.carryValue} ${t("Days")}`: t("Leave.Unlimited")}</Typography>
                        </Grid>
                      </Fragment>
                    ))}
                  </Fragment>
                )}
              </Grid>
            </Grid>
          ))}
        </Grid>
      </AccordionDetails>
    </AccordionStyled>
  );
};

export default AccordionRow;
